import React, { useState } from 'react'
import { ApolloConsumer, Mutation } from 'react-apollo'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  IconSend,
  IconSize
} from '@contrast-security-inc/design-system-foundations'
import { LibraryAgentLanguage } from '../library/library-agent-language'
import {
  Library,
  SendActionMenu,
  SendActionMenuProps
} from './send-action-menu'
import { injector } from '../../../../ng-injector'
import { PureQueryOptions } from 'apollo-client'
import { EmailDomainsQuery } from '../../../../components/security/email-domains-query'
import gql from 'graphql-tag'
import { SendToEmailModal } from 'contrastBase/Modals'
import { RoleSwitch } from 'contrastBase/RoleSwitch'
import { logError, logSuccess } from '../../../../components/toaster'
import { ShareByEmailType } from '../../helpers/ui-interfaces'
import { Role } from '@contrast-security-inc/base-types'
import { ActionButton } from '@contrast-security-inc/design-system-actions'

type Props = Pick<
  SendActionMenuProps,
  'popoverMenuKey' | 'isPanel' | 'libraries' | 'bulk'
> & {
  libraryHashesToExclude?: string[]
  filters?: Filters
  role: Role
  organizationID: string
  showDisabled: boolean
  refresh?: () => Promise<any> | (() => void)
  refetchQueries?: PureQueryOptions[]
  bugtrackerDisabled?: boolean
  shareType: ShareByEmailType
  renderIcon?: boolean
  size?: IconSize
}

interface Filters {
  filterText: string
  quickFilter: string
  aggregations?: Record<string, string[]>
}

/**
 * @desc Returns an array of bugtrackers based on a library's applications
 */
export async function getBugtrackers(BugTrackerFactory, appIDs) {
  const bugtrackers = new Array()
  for (const appID of appIDs) {
    const response = await BugTrackerFactory.getBugtrackersByAppId(appID)
    response.data.bugtrackers.forEach((bugtracker) => {
      if (
        !bugtrackers
          .map((e) => e.bugtracker_id)
          .includes(bugtracker.bugtracker_id)
      ) {
        bugtrackers.push(bugtracker)
      }
    })
  }
  return bugtrackers
}

export const SendAction = ({
  libraries,
  role,
  organizationID,
  showDisabled,
  refetchQueries,
  refresh,
  bugtrackerDisabled,
  filters,
  libraryHashesToExclude,
  shareType,
  renderIcon = true,
  popoverMenuKey,
  size,
  isPanel,
  bulk
}: Props) => {
  const { formatMessage } = useIntl()
  const [emailModalIsOpen, setEmailModalIsOpen] = useState(false)
  const emailMutation = mutationSwitch(shareType)

  return (
    <ApolloConsumer>
      {(apolloClient) => (
        <div>
          <RoleSwitch
            renderDenied={() =>
              showDisabled ? (
                <ActionButton
                  TooltipProps={{
                    body: (
                      <FormattedMessage id="title_libraries_insufficientSendPermissions" />
                    )
                  }}
                  IconButtonProps={{
                    Icon: IconSend,
                    size,
                    'aria-label': 'Send'
                  }}
                />
              ) : undefined
            }
            renderAllowed={() => (
              <SendActionMenu
                popoverMenuKey={popoverMenuKey}
                renderIcon={renderIcon}
                onSelectEmailOption={() =>
                  setEmailModalIsOpen(!emailModalIsOpen)
                }
                libraries={libraries}
                isPanel={isPanel}
                bulk={bulk}
                // librariesAreFromSameApplication={
                //   !bugtrackerDisabled &&
                //   libraries.every((first) =>
                //     libraries.every((second) =>
                //       first.applications
                //         .map((el) => el.appId)
                //         .some((app1) =>
                //           second.applications
                //             .map((el) => el.appId)
                //             .some((app2) => app1 === app2)
                //         )
                //     )
                //   )
                // }
                onSelectBugtrackerOption={
                  !bugtrackerDisabled
                    ? () => {
                        if (!libraries.length) {
                          return
                        }
                        const appIDs = libraries[0].applications.map(
                          (app) => app.appId
                        )

                        injector.invoke([
                          'sendLibService',
                          'BugtrackerFactory',
                          async (sendLibService, BugTrackerFactory) => {
                            const bugtrackers = getBugtrackers(
                              BugTrackerFactory,
                              appIDs
                            )

                            await sendLibService.open({
                              resolve: {
                                libraries: () =>
                                  libraries.map((lib) => ({
                                    hash: lib.hash,
                                    app_language:
                                      typeof lib.language === 'string'
                                        ? LibraryAgentLanguage.fromValue(
                                            lib.language
                                          ).getValue()
                                        : lib.language.getValue()
                                  })),
                                bugtrackers: () => bugtrackers
                              }
                            }).result

                            if (refetchQueries) {
                              await Promise.all(
                                refetchQueries.map(({ query, variables }) => {
                                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                  apolloClient.query({
                                    query,
                                    variables,
                                    fetchPolicy: 'network-only'
                                  })
                                })
                              )
                              // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            }

                            if (refresh) {
                              await refresh()
                            }
                          }
                        ])
                      }
                    : () => undefined
                }
              />
            )}
            role={role}
            requiredRole="EDIT"
          />
          <Mutation<
            {
              _placeholder: boolean
            },
            {
              email: string
              filters?: Filters
              excludedHashes?: string[]
              organizationID: string
              libraries?: Array<{
                sha1: string
                language: string
              }>
            }
          >
            mutation={emailMutation}
          >
            {(sendEmail) => (
              <EmailDomainsQuery organizationID={organizationID}>
                {({ domains }) => (
                  <SendToEmailModal
                    title={formatMessage({
                      id: 'action_libraries_sendLibrary'
                    })}
                    sendItems={async (email) => {
                      try {
                        await sendEmail({
                          variables: graphVariablesSwitch(
                            shareType,
                            email,
                            organizationID,
                            filters,
                            libraryHashesToExclude,
                            libraries
                          )
                        })
                        logSuccess(
                          formatMessage({
                            id: 'detail_libraries_successfullySentLibraryEmail'
                          })
                        )
                      } catch (error) {
                        logError(
                          formatMessage({
                            id: 'detail_libraries_errorSendingLibraryEmail'
                          })
                        )
                        return error
                      }
                    }}
                    onClose={() => setEmailModalIsOpen(false)}
                    isOpen={emailModalIsOpen}
                    permittedDomains={domains}
                  />
                )}
              </EmailDomainsQuery>
            )}
          </Mutation>
        </div>
      )}
    </ApolloConsumer>
  )
}

export const mutationSwitch = (type: ShareByEmailType) => {
  switch (type) {
    case ShareByEmailType.BY_FILTERS:
      return gql`
        mutation sendLibrariesToEmailByFilters(
          $organizationID: ID!
          $email: String!
          $filters: LibraryFilterArg!
          $excludedHashes: [String]!
        ) {
          sendLibrariesToEmailByFilters(
            input: {
              organizationID: $organizationID
              email: $email
              filters: $filters
              excludedHashes: $excludedHashes
            }
          ) {
            _placeholder
          }
        }
      `
    case ShareByEmailType.BY_HASHES:
    default:
      return gql`
        mutation sendLibrariesToEmail(
          $organizationID: ID!
          $email: String!
          $libraries: [Libraries]!
        ) {
          sendLibrariesToEmail(
            input: {
              organizationID: $organizationID
              email: $email
              libraries: $libraries
            }
          ) {
            _placeholder
          }
        }
      `
  }
}

export const graphVariablesSwitch = (
  type: ShareByEmailType,
  email: string,
  organizationID: string,
  filters?: Filters,
  excludedHashes?: string[],
  libraries?: Library[]
) => {
  if (type === ShareByEmailType.BY_HASHES) {
    return {
      email,
      organizationID,
      libraries:
        libraries &&
        libraries.map((lib) => ({
          sha1: lib.hash,
          language:
            typeof lib.language === 'string'
              ? LibraryAgentLanguage.fromValue(lib.language).getName()
              : lib.language.getName()
        }))
    }
  } else if (type === ShareByEmailType.BY_FILTERS && filters) {
    return {
      organizationID,
      email,
      filters: {
        filterText: filters.filterText,
        quickFilter: filters.quickFilter,
        ...filters.aggregations
      },
      excludedHashes
    }
  }
}
